import { isDataset } from "../types/dist/index.js";
import getLogger from "./logger.js";
const logger = getLogger("fromDataSource");
const hasNoQuery = (queryOptions) => !queryOptions.filters && !queryOptions.sortBy && !queryOptions.limit && !queryOptions.offset;
function query(dataset, ...pipeline) {
  return pipeline.reduce((current, fn) => fn(current), dataset);
}
const applyFilters = (filters) => (dataset) => {
  if (!filters || !Array.isArray(filters) || filters.length === 0) {
    logger.warn("No filters provided or filters are not in the correct format.");
    return dataset;
  }
  const columnIndexMap = dataset.columns.reduce((acc, column, index) => {
    acc[column.key] = index;
    return acc;
  }, {});
  const filteredData = dataset.data.filter((row) => filters.every(({ column, op, value }) => {
    const colIndex = columnIndexMap[column];
    if (colIndex === void 0) return true;
    const cellValue = row[colIndex];
    if (cellValue === void 0) return false;
    switch (op) {
      case "eq":
        return cellValue === value;
      case "neq":
        return cellValue !== value;
      case "lt":
        return typeof cellValue === "number" && typeof value === "number" && cellValue < value;
      case "lte":
        return typeof cellValue === "number" && typeof value === "number" && cellValue <= value;
      case "gt":
        return typeof cellValue === "number" && typeof value === "number" && cellValue > value;
      case "gte":
        return typeof cellValue === "number" && typeof value === "number" && cellValue >= value;
      case "in":
        return Array.isArray(value) ? value.includes(cellValue) : typeof cellValue === "string" && cellValue.includes(value);
      case "nin":
        return Array.isArray(value) ? !value.includes(cellValue) : typeof cellValue === "string" && !cellValue.includes(value);
      default:
        return false;
    }
  }));
  return { columns: dataset.columns, data: filteredData };
};
const applySortBy = (sortBy) => (dataset) => {
  if (!sortBy) return dataset;
  const [sortColumn, sortOrder = "ASC"] = sortBy.split(":");
  const columnIndex = dataset.columns.findIndex((col) => col.key === sortColumn);
  if (columnIndex === -1) return dataset;
  const sortedData = [...dataset.data].sort((a, b) => {
    const valA = a[columnIndex];
    const valB = b[columnIndex];
    if (typeof valA === "number" && typeof valB === "number") {
      return sortOrder.toUpperCase() === "DESC" ? valB - valA : valA - valB;
    } else if (typeof valA === "string" && typeof valB === "string") {
      return sortOrder.toUpperCase() === "DESC" ? valB.localeCompare(valA) : valA.localeCompare(valB);
    }
    if ((valA === void 0 || valA === null) && (valB === void 0 || valB === null)) {
      return 0;
    }
    if (valA === void 0 || valA === null) {
      return 1;
    }
    if (valB === void 0 || valB === null) {
      return -1;
    }
    return 0;
  });
  return { columns: dataset.columns, data: sortedData };
};
const applyLimitAndOffset = ({ limit = void 0, offset = void 0 }) => (dataset) => {
  if (limit && typeof limit === "number" && (offset && typeof offset === "number")) {
    return {
      ...dataset,
      data: dataset.data.slice(offset, offset + limit)
    };
  }
  if (limit && typeof limit === "number" && (!offset || offset === 0)) {
    return {
      ...dataset,
      data: dataset.data.slice(0, limit)
    };
  }
  if (!limit && limit === void 0 && (offset && typeof offset === "number")) {
    return {
      ...dataset,
      data: dataset.data.slice(offset)
    };
  }
  return dataset;
};
function fromDataSource(queryOptions, dataset) {
  logger.debug("fromDataSource", { queryOptions, dataset });
  if (!dataset) {
    throw new Error("Dataset is required");
  }
  if (hasNoQuery(queryOptions) || !isDataset(dataset)) {
    logger.debug("No query options provided or dataset is not valid, returning original dataset");
    return dataset;
  }
  const {
    filters,
    sortBy = "",
    limit,
    offset
  } = queryOptions;
  logger.debug("Applying query options", { filters, sortBy, limit, offset });
  return query(
    dataset,
    // select * from <dataset>
    applyFilters(filters),
    // where ...
    applySortBy(sortBy),
    // sort by ...
    applyLimitAndOffset({ limit, offset })
    // limit ... offset ...
  );
}
export {
  applyFilters,
  applyLimitAndOffset,
  applySortBy,
  fromDataSource,
  hasNoQuery,
  query
};
